// src/presentation/components/PatientsHeader.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import { Filter, Plus } from "lucide-react";
import { SearchInput } from "@/components/custom/SearchInput";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PermissionGuard } from "./PermissionGuard";
import { textStyles } from "@/lib/textStyles";

interface PatientsHeaderProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onAddPatient: () => void;
  onFilterChange?: (filter: string) => void;
  activeFilter?: string;
  canCreate?: boolean;
}

export const PatientsHeader: React.FC<PatientsHeaderProps> = ({
  searchQuery,
  onSearchChange,
  onAddPatient,
  onFilterChange,
  activeFilter,
  canCreate = true,
}) => {
  const { t } = useTranslation("patients");
  const sortOptions = [
    { label: t("filter.newest"), value: "newest" },
    { label: t("filter.oldest"), value: "oldest" },
    { label: t("filter.nameAsc"), value: "nameAsc" },
    { label: t("filter.nameDesc"), value: "nameDesc" },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-gray-900" style={textStyles.heading2}>
            {t("pageTitle")}
          </h1>
        </div>
      </div>

      <div className="sticky top-0 z-30 -mx-4 border-y border-gray-100 bg-white/95 p-3 shadow-sm backdrop-blur sm:-mx-5 md:static md:mx-0 md:rounded-2xl md:border md:p-2 md:shadow-none lg:p-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-2 md:max-w-xl">
            <SearchInput
              containerClassName="min-w-0 flex-1"
              className="rounded-xl border-gray-200 bg-white pl-10 shadow-none"
              name="patients-search"
              placeholder={t("search.placeholder")}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />

            {/* Filter Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="h-10 shrink-0 gap-2 rounded-xl bg-white cursor-pointer"
                >
                  <Filter className="mr-2 w-4 h-4" />
                  <span className="hidden sm:inline">{t("filter.label")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="bottom" className="w-56">
                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">
                  {t("filter.sortBy")}
                </div>
                {sortOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => onFilterChange?.(option.value)}
                    className={`cursor-pointer ${
                      option.value === activeFilter ? "text-primary" : "text-gray-700"
                    }`}
                  >
                    {option.label}
                  </DropdownMenuItem>
                ))}
                {/* <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">
                GENDER
              </div>
              {genderOptions.map((option) => (
                <DropdownMenuItem
                  key={option}
                  onClick={() => onFilterChange?.(option)}
                  className="cursor-pointer"
                >
                  {option}
                </DropdownMenuItem>
              ))} */}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Add Patient Button */}
          <PermissionGuard allowed={canCreate}>
            <Button
              onClick={onAddPatient}
              className="h-10 shrink-0 gap-2 rounded-xl bg-primary text-white hover:bg-primary cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              {t("addPatient")}
            </Button>
          </PermissionGuard>
        </div>
      </div>
    </div>
  );
};
